import React, { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import Context from "../../Context";
import constant, { statuses } from "../../constants";
import { resizeImage } from "../../util";
import {
  Avatar,
  AvatarImage,
  useToast,
  Spinner,
  ScrollArea,
} from "../../components/ui";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components";
import { Header } from "../../components/modules";

// React Icons
import {
  FiPackage,
  FiLayers,
  FiUser,
  FiPhone,
  FiCreditCard,
  FiMapPin,
  FiUploadCloud,
  FiTrash2,
  FiCheckCircle,
  FiCheck,
  FiX,
  FiClock,
  FiTag,
  FiAlertCircle,
} from "react-icons/fi";
import { RiCoupon2Line } from "react-icons/ri";
import { FaCheckCircle, FaTimes } from "react-icons/fa";

export const CreateCouponOrder = () => {
  const { toast } = useToast();
  const { userId } = useParams();
  const { callApi, navigate } = Context();

  const [user, setUser] = useState(null);
  const [coupons, setCoupons] = useState(null);
  const [bundles, setBundles] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);
  const [loadingData, setLoadingData] = useState(true);

  useEffect(() => {
    fetchInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const fetchInitialData = async () => {
    setLoadingUser(true);
    setLoadingData(true);

    // 1. Fetch User Info
    const userRes = await callApi(
      "users-getUserInfoAdmin",
      { userId },
      { notShowLoading: true }
    );

    if (userRes?.status && userRes?.data) {
      setUser(userRes.data);
    } else {
      toast({
        className: "bg-rose-600 text-white",
        title: (
          <div className="flex gap-2 items-center text-sm font-semibold">
            <FaTimes /> ไม่พบข้อมูลผู้ใช้งาน
          </div>
        ),
      });
      navigate("/users");
      return;
    }
    setLoadingUser(false);

    // 2. Fetch Coupons & Bundles
    const [couponsRes, bundlesRes] = await Promise.all([
      callApi(
        "coupons-getAllCoupon",
        { queries: [["status", "!=", statuses.INACTIVE]] },
        { notShowLoading: true }
      ),
      callApi(
        "coupons-getAllBundles",
        { queries: [["status", "!=", statuses.INACTIVE]] },
        { notShowLoading: true }
      ),
    ]);

    setCoupons(couponsRes || []);
    setBundles(bundlesRes || []);
    setLoadingData(false);
  };

  return (
    <div className="h-fit w-full max-w-[1600px] mx-auto pb-12 min-w-0 px-4">
      {/* Header */}
      <Header
        header="Coupons"
        subHeader={`สร้างคำสั่งซื้อคูปอง / ลูกค้า: ${userId}`}
        icon={<RiCoupon2Line className="text-blue" />}
      />

      {loadingUser ? (
        <div className="flex flex-col h-[400px] justify-center items-center gap-3 bg-white rounded-md shadow-sm border border-[#E5E7E9] mt-4">
          <Spinner />
          <p className="text-sm font-medium text-slate-500">กำลังโหลดข้อมูลลูกค้า...</p>
        </div>
      ) : user ? (
        <div className="mt-4 space-y-4">
          {/* User Profile Banner */}
          <UserProfileBanner user={user} />

          {/* Main Coupon Order Creation Box */}
          <div className="bg-white rounded-md shadow-sm border border-[#E5E7E9] overflow-hidden">
            {loadingData ? (
              <div className="flex flex-col h-[400px] justify-center items-center gap-3">
                <Spinner />
                <p className="text-sm font-medium text-slate-500">กำลังโหลดรายการคูปองและแพ็กเกจ...</p>
              </div>
            ) : (
              <CouponSelectSection
                coupons={coupons || []}
                bundles={bundles || []}
                user={user}
              />
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

// ── User Profile Banner Component ─────────────────────────────────────────────
const UserProfileBanner = ({ user }) => {
  const isRemoved = user.status === statuses.REMOVED;
  const isInactive = user.status === statuses.INACTIVE;

  const statusColor = isRemoved
    ? "bg-rose-500"
    : isInactive
    ? "bg-amber-500"
    : "bg-emerald-500";

  const statusText = isRemoved
    ? "ถูกลบ/ระงับ"
    : isInactive
    ? "ปิดใช้งาน"
    : "ปกติ (Active)";

  const shippingAddr = user?.address?.addr
    ? `${user.address.addr} ${user.address.province || ""} ${user.address.zipcode || ""}`.trim()
    : "ไม่มีข้อมูลที่อยู่จัดส่ง";

  return (
    <div className="bg-white rounded-md shadow-sm border border-[#E5E7E9] p-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
      <div className="flex items-center gap-4">
        <div className="relative">
          <Avatar className="w-16 h-16 border-2 border-slate-100 shadow-sm">
            <AvatarImage src={user?.profileImage || "https://github.com/shadcn.png"} />
          </Avatar>
          <span
            className={`w-4 h-4 rounded-full border-2 border-white absolute bottom-0 right-0 ${statusColor}`}
            title={statusText}
          />
        </div>

        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-bold text-slate-800">
              {user?.firstName} {user?.lastName}
            </h2>
            <span
              className={`text-xs px-2 py-0.5 rounded-full font-semibold text-white ${statusColor}`}
            >
              {statusText}
            </span>
            {user?.userNo && (
              <span className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-600 font-mono font-bold">
                No. {user.userNo}
              </span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500 mt-1">
            <span className="flex items-center gap-1 font-mono">
              <FiUser className="text-slate-400" /> ID: {user?.userId}
            </span>
            <span className="flex items-center gap-1 font-mono">
              <FiPhone className="text-slate-400" /> {user?.phone || "-"}
            </span>
            {user?.bank?.name && (
              <span className="flex items-center gap-1">
                <FiCreditCard className="text-slate-400" /> {user.bank.name} - {user.bank.accNo} ({user.bank.accName})
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="bg-slate-50 border border-slate-200 rounded-md p-2.5 text-xs text-slate-600 max-w-md w-full md:w-auto">
        <div className="flex items-center gap-1.5 font-semibold text-slate-700 mb-0.5">
          <FiMapPin className="text-emerald-600" /> ที่อยู่จัดส่งหลัก
        </div>
        <p className="truncate text-slate-500" title={shippingAddr}>
          {shippingAddr}
        </p>
      </div>
    </div>
  );
};

// ── Main Coupon & Bundle Selector Section ─────────────────────────────────────
const CouponSelectSection = ({ coupons = [], bundles = [], user }) => {
  const { toast } = useToast();
  const { callApi } = Context();

  const [selectionType, setSelectionType] = useState("coupon"); // 'coupon' | 'bundle'
  const [selectedCouponId, setSelectedCouponId] = useState("");
  const [selectedBundleId, setSelectedBundleId] = useState("");

  const [previewImage, setPreviewImage] = useState(false);
  const [image64, setImage64] = useState(false);
  const [loading, setLoading] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  const isBundle = selectionType === "bundle";

  const selectedCoupon = useMemo(() => {
    return coupons.find((c) => c.id === selectedCouponId) || null;
  }, [coupons, selectedCouponId]);

  const selectedBundle = useMemo(() => {
    return bundles.find((b) => b.id === selectedBundleId) || null;
  }, [bundles, selectedBundleId]);

  const selectedItem = isBundle ? selectedBundle : selectedCoupon;
  const bundleItems = selectedBundle?.items || [];

  const handleTabChange = (type) => {
    setSelectionType(type);
    setSelectedCouponId("");
    setSelectedBundleId("");
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validTypes = ["image/jpeg", "image/png", "image/jpg", "image/webp"];
    if (!validTypes.includes(file.type)) {
      toast({
        className: "bg-rose-600 text-white",
        title: (
          <div className="flex gap-2 items-center text-sm font-semibold">
            <FaTimes /> รองรับเฉพาะไฟล์รูปภาพ (JPG, PNG, WEBP) เท่านั้น
          </div>
        ),
      });
      return;
    }

    try {
      const resizedBase64 = await resizeImage(file);
      if (resizedBase64) {
        const previewUrl = URL.createObjectURL(file);
        setPreviewImage(previewUrl);
        setImage64(resizedBase64);
      }
    } catch (err) {
      console.error("Failed to resize image:", err);
      toast({
        className: "bg-rose-600 text-white",
        title: "เกิดข้อผิดพลาดในการประมวลผลรูปภาพ",
      });
    }
  };

  const handleRemoveSlip = () => {
    setPreviewImage(false);
    setImage64(false);
  };

  const handleCreateOrder = async () => {
    setConfirmModalOpen(false);
    setLoading(true);

    const apiName = isBundle
      ? "coupons-createBundleTransaction"
      : "coupons-createCouponTransaction";

    const payload = isBundle
      ? {
          userId: user.userId,
          id: selectedBundle.id,
          slip: image64,
          createdByAdmin: true,
        }
      : {
          userId: user.userId,
          id: selectedCoupon.id,
          slip: image64,
        };

    const { status, orderId, toastMessage } = await callApi(apiName, payload);

    if (status) {
      toast({
        className: "bg-green text-white",
        title: (
          <div className="flex gap-2 items-center text-sm font-semibold">
            <FaCheckCircle /> สร้างคำสั่งซื้อคูปองสำเร็จ (รหัส: {orderId})
          </div>
        ),
      });

      window.open(
        isBundle ? `/coupon/bundle/${orderId}` : `/coupon/${orderId}`,
        "_blank"
      );
      window.location.reload();
    } else {
      toast({
        className: "bg-rose-600 text-white",
        title: (
          <div className="flex gap-2 items-center text-sm font-semibold">
            <FaTimes /> {toastMessage || "ไม่สามารถสร้างคำสั่งซื้อได้"}
          </div>
        ),
      });
    }
    setLoading(false);
  };

  return (
    <div className="p-4 space-y-5">
      {/* Title & Mode Switcher */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-slate-200">
        <div>
          <h3 className="text-base font-bold text-slate-800">
            เลือกประเภทคูปองที่ต้องการสร้างออเดอร์
          </h3>
          <p className="text-xs text-slate-500">
            เลือกคูปองเดี่ยว หรือเซ็ตแพ็กเกจคูปอง และแนบหลักฐานสลิปการชำระเงิน
          </p>
        </div>

        {/* Tabs */}
        <div className="flex bg-slate-100 p-1 rounded-md border border-slate-200">
          <button
            type="button"
            className={`flex items-center gap-1.5 px-4 py-2 rounded-md font-semibold text-xs transition-all ${
              !isBundle
                ? "bg-blue text-white shadow-sm"
                : "text-slate-600 hover:text-slate-900"
            }`}
            onClick={() => handleTabChange("coupon")}
          >
            <RiCoupon2Line className="text-sm" />
            <span>คูปองเดี่ยว</span>
          </button>
          <button
            type="button"
            className={`flex items-center gap-1.5 px-4 py-2 rounded-md font-semibold text-xs transition-all ${
              isBundle
                ? "bg-blue text-white shadow-sm"
                : "text-slate-600 hover:text-slate-900"
            }`}
            onClick={() => handleTabChange("bundle")}
          >
            <FiPackage className="text-sm" />
            <span>แพ็กเกจคูปอง</span>
          </button>
        </div>
      </div>

      {/* Grid: Left (Selection & Info) / Right (Slip Upload) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* ── LEFT: SELECTION & DETAILS (Cols 7) ── */}
        <div className="lg:col-span-7 space-y-4">
          {/* Dropdown Selector */}
          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1.5">
              {isBundle ? "เลือกแพ็กเกจคูปอง" : "เลือกคูปองเดี่ยว"}
            </label>
            <select
              className="w-full border border-gray-300 py-2.5 px-3 font-semibold text-sm rounded-md bg-white focus:ring-1 focus:ring-blue focus:border-blue outline-none"
              value={isBundle ? selectedBundleId : selectedCouponId}
              onChange={(e) => {
                if (isBundle) {
                  setSelectedBundleId(e.target.value);
                } else {
                  setSelectedCouponId(e.target.value);
                }
              }}
            >
              <option value="">-- กรุณาเลือกรายการ --</option>
              {(isBundle ? bundles : coupons)
                .slice()
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name} — {(item.price || 0).toLocaleString()} ฿
                  </option>
                ))}
            </select>
          </div>

          {/* Selected Item Overview Card */}
          {selectedItem ? (
            <div className="bg-slate-50 border border-slate-200 rounded-md p-4 space-y-3">
              <div className="flex items-start justify-between gap-2 border-b border-slate-200 pb-3">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-0.5 rounded text-xs font-bold bg-blue text-white">
                      {isBundle ? "แพ็กเกจ" : constant.couponTypeTranslated[selectedItem.type] || "คูปอง"}
                    </span>
                    <h4 className="font-bold text-base text-slate-800">
                      {selectedItem.name}
                    </h4>
                  </div>
                  {selectedItem.description && (
                    <p className="text-xs text-slate-500 mt-1">
                      {selectedItem.description}
                    </p>
                  )}
                </div>

                <div className="text-right">
                  <span className="text-xs text-slate-400 block">ราคาชำระ</span>
                  <span className="text-xl font-extrabold text-green">
                    {(selectedItem.price || 0).toLocaleString()} ฿
                  </span>
                </div>
              </div>

              {/* Info Stats */}
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div className="bg-white p-2.5 rounded border border-slate-200">
                  <span className="text-slate-400 block mb-0.5">
                    {isBundle ? "จำนวนคูปองรวม" : "วันหมดอายุ"}
                  </span>
                  <span className="font-bold text-slate-700 flex items-center gap-1">
                    {isBundle ? (
                      <>
                        <FiLayers className="text-blue" />{" "}
                        {bundleItems.reduce(
                          (acc, it) => acc + Number(it.quantity || 0),
                          0
                        )}{" "}
                        ใบ
                      </>
                    ) : selectedItem.expireAt ? (
                      <>
                        <FiClock className="text-amber-600" />{" "}
                        {new Date(selectedItem.expireAt).toLocaleDateString(
                          "th-TH",
                          {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          }
                        )}
                      </>
                    ) : (
                      "ไม่มีวันหมดอายุ"
                    )}
                  </span>
                </div>

                <div className="bg-white p-2.5 rounded border border-slate-200">
                  <span className="text-slate-400 block mb-0.5">รหัสรายการ</span>
                  <span className="font-mono font-semibold text-slate-600 truncate block" title={selectedItem.id}>
                    {selectedItem.id}
                  </span>
                </div>
              </div>

              {/* Bundle Contents Table */}
              {isBundle && bundleItems.length > 0 && (
                <div className="space-y-1.5 pt-1">
                  <span className="text-xs font-bold text-slate-700 flex items-center gap-1">
                    <FiLayers className="text-blue" /> รายการคูปองในแพ็กเกจ ({bundleItems.length} ชนิด)
                  </span>
                  <div className="border border-slate-200 rounded overflow-hidden bg-white">
                    <Table>
                      <TableHeader className="bg-slate-100">
                        <TableRow>
                          <TableHead className="text-xs font-bold">ชื่อคูปอง</TableHead>
                          <TableHead className="text-xs font-bold text-center">ประเภท</TableHead>
                          <TableHead className="text-xs font-bold text-center">จำนวน</TableHead>
                          <TableHead className="text-xs font-bold text-right pr-4">ราคาต่อใบ</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {bundleItems.map((it, idx) => (
                          <TableRow key={`${it.id || it.couponId}-${idx}`} className="hover:bg-slate-50">
                            <TableCell className="text-xs font-semibold text-slate-800">
                              {it.name || "-"}
                            </TableCell>
                            <TableCell className="text-xs text-center text-slate-600">
                              {constant.couponTypeTranslated[it.type] || it.type || "-"}
                            </TableCell>
                            <TableCell className="text-xs font-bold text-center text-blue">
                              {it.quantity || 1} ใบ
                            </TableCell>
                            <TableCell className="text-xs font-semibold text-right pr-4 text-slate-700">
                              {(it.price || 0).toLocaleString()} ฿
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="h-[220px] flex flex-col justify-center items-center gap-2 border border-dashed border-slate-200 rounded-md bg-slate-50 text-slate-400">
              <RiCoupon2Line className="text-3xl text-slate-300" />
              <p className="text-sm font-semibold">กรุณาเลือกรายการด้านบน</p>
              <p className="text-xs">รายละเอียดและราคาจะปรากฏที่นี่</p>
            </div>
          )}
        </div>

        {/* ── RIGHT: SLIP UPLOAD & PAYMENT (Cols 5) ── */}
        <div className="lg:col-span-5 space-y-4 flex flex-col justify-between">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                <FiUploadCloud className="text-blue text-sm" /> แนบหลักฐานสลิปการโอนเงิน
              </label>
              {image64 && (
                <span className="text-xs font-bold text-emerald-600 flex items-center gap-1">
                  <FiCheck /> แนบสลิปแล้ว
                </span>
              )}
            </div>

            {/* Upload Area / Preview */}
            {previewImage ? (
              <div className="bg-slate-50 border border-slate-200 rounded-md p-3 flex flex-col items-center gap-3">
                <div className="relative group max-w-[260px] max-h-[380px] overflow-hidden rounded border border-slate-300 shadow-sm bg-white">
                  <img
                    src={previewImage}
                    alt="Payment Slip Preview"
                    className="w-full h-auto object-contain max-h-[380px]"
                  />
                </div>
                <button
                  type="button"
                  disabled={loading}
                  className="inline-flex items-center gap-1.5 py-1.5 px-3 bg-rose-50 text-rose-600 hover:bg-rose-100 border border-rose-200 rounded-md text-xs font-semibold transition-all"
                  onClick={handleRemoveSlip}
                >
                  <FiTrash2 /> ลบรูปภาพสลิป
                </button>
              </div>
            ) : (
              <label
                htmlFor="coupon-slip-input"
                className="flex flex-col items-center justify-center gap-2.5 p-8 border-2 border-dashed border-blue/30 rounded-md bg-blue/5 hover:bg-blue/10 cursor-pointer transition-all text-center group"
              >
                <div className="w-12 h-12 rounded-full bg-blue/10 flex items-center justify-center text-blue group-hover:scale-110 transition-transform">
                  <FiUploadCloud className="text-2xl" />
                </div>
                <div>
                  <p className="font-bold text-sm text-slate-700">คลิกเพื่ออัปโหลดรูปสลิป</p>
                  <p className="text-xs text-slate-400 mt-0.5">รองรับไฟล์ JPG, PNG หรือ WEBP</p>
                </div>
                <input
                  id="coupon-slip-input"
                  type="file"
                  accept="image/png, image/jpeg, image/jpg, image/webp"
                  className="hidden"
                  onChange={handleFileUpload}
                />
              </label>
            )}

            {!image64 && (
              <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 p-2 rounded flex items-center gap-1.5">
                <FiAlertCircle className="text-sm shrink-0" />
                <span>จำเป็นต้องแนบสลิปเพื่อยืนยันการชำระเงินก่อนสร้างออเดอร์</span>
              </p>
            )}
          </div>

          {/* Action Button */}
          <div className="pt-4 border-t border-slate-200">
            <button
              type="button"
              disabled={!selectedItem || !image64 || loading}
              className="w-full bg-green text-white py-3 px-4 rounded-md font-bold text-base hover:opacity-90 shadow-md transition-all flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
              onClick={() => setConfirmModalOpen(true)}
            >
              {loading ? (
                <Spinner className="w-5 h-5 text-white" />
              ) : (
                <>
                  <RiCoupon2Line className="text-xl" />
                  <span>
                    สร้างออเดอร์คูปอง —{" "}
                    {(selectedItem?.price || 0).toLocaleString()} ฿
                  </span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {confirmModalOpen && (
        <CouponOrderConfirmationModal
          onClose={() => setConfirmModalOpen(false)}
          onConfirm={handleCreateOrder}
          loading={loading}
          user={user}
          selectedItem={selectedItem}
          isBundle={isBundle}
          previewImage={previewImage}
        />
      )}
    </div>
  );
};

// ── Coupon Order Confirmation Modal Component ─────────────────────────────────
const CouponOrderConfirmationModal = ({
  onClose,
  onConfirm,
  loading,
  user,
  selectedItem,
  isBundle,
  previewImage,
}) => {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-md shadow-xl border border-slate-200 w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-150">
        {/* Modal Header */}
        <div className="bg-slate-50 border-b border-slate-200 px-5 py-3.5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FiCheckCircle className="text-emerald-600 text-lg" />
            <h3 className="font-bold text-base text-slate-800">
              ยืนยันการสร้างคำสั่งซื้อคูปอง
            </h3>
          </div>
          <button
            type="button"
            disabled={loading}
            className="text-slate-400 hover:text-slate-600 p-1 rounded"
            onClick={onClose}
          >
            <FiX className="text-lg" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-5 space-y-3.5 text-sm">
          <div className="bg-slate-50 p-3 rounded-md border border-slate-200 space-y-2">
            <div className="flex justify-between text-xs">
              <span className="text-slate-500">ลูกค้าผู้ซื้อ:</span>
              <span className="font-bold text-slate-800">
                {user?.firstName} {user?.lastName} ({user?.userId})
              </span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-slate-500">เบอร์โทรศัพท์:</span>
              <span className="font-mono font-semibold text-slate-800">
                {user?.phone || "-"}
              </span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-slate-500">ประเภท:</span>
              <span className="font-semibold text-blue">
                {isBundle ? "แพ็กเกจคูปอง" : "คูปองเดี่ยว"}
              </span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-slate-500">รายการ:</span>
              <span className="font-bold text-slate-800">
                {selectedItem?.name}
              </span>
            </div>
          </div>

          {/* Slip thumbnail preview */}
          {previewImage && (
            <div className="flex items-center gap-3 bg-slate-50 p-2.5 rounded border border-slate-200">
              <img
                src={previewImage}
                alt="slip thumb"
                className="w-12 h-16 object-cover rounded border border-slate-300"
              />
              <div className="text-xs">
                <span className="font-bold text-slate-700 block">สลิปการโอนเงิน</span>
                <span className="text-emerald-600 font-semibold flex items-center gap-1">
                  <FiCheck /> แนบพร้อมสร้างออเดอร์
                </span>
              </div>
            </div>
          )}

          <div className="border border-emerald-100 bg-emerald-50/50 p-3.5 rounded-md flex justify-between items-center">
            <div>
              <p className="text-xs text-slate-500">ยอดชำระสุทธิ</p>
              <p className="text-xl font-extrabold text-green">
                {(selectedItem?.price || 0).toLocaleString()} ฿
              </p>
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="bg-slate-50 border-t border-slate-200 px-5 py-3 flex gap-2 justify-end">
          <button
            type="button"
            disabled={loading}
            className="px-4 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-200 rounded-md transition-all"
            onClick={onClose}
          >
            ยกเลิก
          </button>
          <button
            type="button"
            disabled={loading}
            className="px-5 py-2 text-xs font-bold text-white bg-green hover:opacity-90 rounded-md shadow-sm transition-all flex items-center gap-1.5"
            onClick={onConfirm}
          >
            {loading ? <Spinner className="w-4 h-4 text-white" /> : <FiCheck />}
            <span>ยืนยันสร้างคำสั่งซื้อ</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateCouponOrder;
